import React, { useState, useEffect } from 'react';
import { isOverdue } from '../lib/utils';

export default function KpiStrip({ tasks, month }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const mt = tasks.filter(t => t.month === month);
  const total   = mt.length;
  const done    = mt.filter(t => t.status === 'done').length;
  const wip     = mt.filter(t => t.status === 'in_progress' && !isOverdue(t)).length;
  const pending = mt.filter(t => t.status === 'pending').length;
  const overdue = mt.filter(t => isOverdue(t)).length;
  const pct     = total ? Math.round((done / total) * 100) : 0;

  if (isMobile) {
    return (
      <div style={{ padding:'0 16px 20px' }}>
        {/* Featured full-width on mobile */}
        <div style={{ ...styles.kpi, ...styles.featured, marginBottom:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ ...styles.label, color:'rgba(255,255,255,.55)', marginBottom:6 }}>Overall · {capitalize(month)}</div>
              <div style={{ display:'flex', alignItems:'baseline', gap:4 }}>
                <span style={{ fontFamily:"'Fraunces',serif", fontSize:42, fontWeight:500, letterSpacing:'-0.03em', color:'white' }}>{pct}</span>
                <span style={{ fontSize:16, color:'rgba(255,255,255,.5)' }}>%</span>
              </div>
              <div style={{ fontSize:11.5, color:'rgba(255,255,255,.6)', marginTop:2 }}>{done} of {total} tasks shipped</div>
            </div>
            {/* Mini stat pills on the right */}
            <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' }}>
              <MiniPill label="Pending" value={pending} color="#94A3B8" />
              <MiniPill label="In progress" value={wip} color="#F59E0B" />
              <MiniPill label="Done" value={done} color="#10B981" />
              <MiniPill label="Overdue" value={overdue} color="#EF4444" />
            </div>
          </div>
          <div style={styles.barTrack}>
            <div style={{ ...styles.barFill, width:`${pct}%` }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.grid}>
      <div style={{ ...styles.kpi, ...styles.featured }}>
        <div style={{ ...styles.label, color:'rgba(255,255,255,.55)' }}>Overall Completion · {capitalize(month)}</div>
        <div style={styles.bigVal}>
          <span style={{ fontFamily:"'Fraunces',serif", fontSize:42, fontWeight:500, letterSpacing:'-0.03em' }}>{pct}</span>
          <span style={{ fontSize:18, color:'rgba(255,255,255,.5)', marginLeft:4 }}>%</span>
        </div>
        <div style={{ ...styles.meta, color:'rgba(255,255,255,.65)' }}>{done} of {total} tasks shipped this month</div>
        <div style={styles.barTrack}>
          <div style={{ ...styles.barFill, width:`${pct}%`, background:'linear-gradient(90deg,#7DD3FC,#86EFAC)' }} />
        </div>
      </div>
      <KpiCard label="Pending" dot="#94A3B8" value={pending} meta="awaiting kickoff" />
      <KpiCard label="In Progress" dot="#F59E0B" value={wip} meta="active execution" />
      <KpiCard label="Done" dot="#10B981" value={done} meta="shipped & verified" />
      <KpiCard label="Overdue" dot="#EF4444" value={overdue} meta="needs new ETA" valueColor="#EF4444" />
    </div>
  );
}

function MiniPill({ label, value, color }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:5, background:'rgba(255,255,255,.12)', borderRadius:99, padding:'3px 10px' }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:color, display:'inline-block', flexShrink:0 }} />
      <span style={{ fontSize:10.5, color:'rgba(255,255,255,.7)', fontFamily:"'JetBrains Mono',monospace" }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:600, color:'white', marginLeft:2 }}>{value}</span>
    </div>
  );
}

function KpiCard({ label, dot, value, meta, valueColor }) {
  return (
    <div style={styles.kpi} onMouseEnter={e => e.currentTarget.style.transform='translateY(-1px)'} onMouseLeave={e => e.currentTarget.style.transform='none'}>
      <div style={styles.label}>
        <span style={{ width:7, height:7, borderRadius:'50%', background:dot, display:'inline-block', marginRight:6 }} />
        {label}
      </div>
      <div style={{ ...styles.bigVal, color: valueColor || '#0A2540' }}>
        <span style={{ fontFamily:"'Fraunces',serif", fontSize:42, fontWeight:500, letterSpacing:'-0.03em' }}>{value}</span>
      </div>
      <div style={styles.meta}>{meta}</div>
    </div>
  );
}

const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1);

const styles = {
  grid: { display:'grid', gridTemplateColumns:'1.4fr 1fr 1fr 1fr 1fr', gap:14, padding:'0 32px 28px' },
  kpi: {
    background:'white', border:'1px solid #E4ECF4', borderRadius:20,
    padding:'20px 22px', position:'relative', overflow:'hidden',
    transition:'all .25s ease', cursor:'default',
  },
  featured: {
    background:'linear-gradient(135deg,#0A2540 0%,#16385C 100%)',
    color:'white', border:'none',
  },
  label: { fontSize:10.5, textTransform:'uppercase', letterSpacing:'0.14em', color:'#7A8BA3', fontFamily:"'JetBrains Mono',monospace", marginBottom:14, display:'flex', alignItems:'center' },
  bigVal: { lineHeight:1, marginBottom:10 },
  meta: { fontSize:12, color:'#3D5878' },
  barTrack: { height:5, background:'rgba(255,255,255,.12)', borderRadius:99, marginTop:14, overflow:'hidden' },
  barFill: { height:'100%', borderRadius:99, background:'linear-gradient(90deg,#7DD3FC,#86EFAC)', transition:'width .8s cubic-bezier(.4,0,.2,1)' },
};
